import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Link, Outlet } from 'react-router-dom';
import {
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
} from '../../icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
const avatarImage = '/avatar.jpg';

/* ─── Hamburger ─────────────────────────────────────────── */
const MenuIcon = () => (
  <span className="flex flex-col justify-center gap-1 w-5 h-5" aria-hidden="true">
    <span className="block h-0.5 w-full bg-current rounded-full" />
    <span className="block h-0.5 w-full bg-current rounded-full" />
    <span className="block h-0.5 w-full bg-current rounded-full" />
  </span>
);

/* ─── Dark-mode toggle ───────────────────────────────────── */
const DarkToggle = () => {
  const { dark, toggleDark } = useTheme();
  return (
    <button
      type="button"
      onClick={toggleDark}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Light mode' : 'Dark mode'}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border-2 border-transparent
        transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-matdash-primary
        ${dark ? 'bg-matdash-primary' : 'bg-matdash-border'}`}
    >
      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
        ${dark ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  );
};

const SunIcon = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M12 3v1m0 16v1m8.66-9h-1M4.34 12h-1m15.07-6.07-.7.7M5.63 18.37l-.7.7m12.02 0-.7-.7M5.63 5.63l-.7-.7M12 7a5 5 0 1 0 0 10A5 5 0 0 0 12 7z" />
  </svg>
);
const MoonIcon = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
  </svg>
);

/* ─── Horizontal nav link ────────────────────────────────── */
const ShellNavLink = ({ to, label, icon: Icon, end }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => `md-navbar-link${isActive ? ' md-navbar-link-active' : ''}`}
  >
    {Icon && <Icon className="h-4 w-4 flex-shrink-0 opacity-90" />}
    <span>{label}</span>
  </NavLink>
);

/* ─── Mobile nav link ────────────────────────────────────── */
const MobileNavLink = ({ to, label, icon: Icon, end, onClick }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    className={({ isActive }) => `md-sidebar-link${isActive ? ' md-sidebar-link-active' : ''}`}
  >
    {Icon && <Icon className="h-4 w-4 flex-shrink-0 opacity-90" />}
    <span>{label}</span>
  </NavLink>
);

/* ══════════════════════════════════════════════════════════
   AdminShell
   props:
     portalLabel      "Court Admin" | "Platform Admin"
     portalSub        court name or "Snwolley AI"
     navItems         [{ to, label, icon, end? }]
     accountSettingsPath  "/admin/account-settings" | "/super-admin/account-settings"
   ══════════════════════════════════════════════════════════ */
const AdminShell = ({
  portalLabel,
  portalSub,
  navItems = [],
  accountSettingsPath = '/account-settings',
}) => {
  const { user, logout } = useAuth();
  const { dark } = useTheme();

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profileOpen, setProfileOpen]     = useState(false);
  const [signOutOpen, setSignOutOpen]     = useState(false);
  const profileRef = useRef(null);

  const userName = user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
    : 'User';

  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const closeMobile = () => setMobileNavOpen(false);
  const handleConfirmSignOut = () => {
    setSignOutOpen(false);
    setProfileOpen(false);
    logout();
  };

  return (
    <div className={`min-h-screen md-app-shell flex flex-col ${dark ? 'bg-gray-950' : 'bg-matdash-body'}`}>

      {/* ── Top bar ─────────────────────────────────────────── */}
      <header className={`md-topbar sticky top-0 z-40 ${dark ? '!bg-gray-900 !border-gray-800' : ''}`}>
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between gap-4 py-3 border-b border-matdash-border">

            {/* Logo + portal name */}
            <Link to="." className="flex items-center gap-2 min-w-0 shrink-0">
              <img src="/logo.png" alt="" className="h-8 w-8 rounded-lg" />
              <div className="min-w-0 hidden sm:block">
                <p className={`text-sm font-bold truncate leading-tight ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                  CourtScribe
                </p>
                <p className={`text-[11px] truncate ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>
                  {portalLabel} · {portalSub}
                </p>
              </div>
            </Link>

            {/* Right controls */}
            <div className="flex items-center gap-2 sm:gap-3">

              {/* Dark mode toggle */}
              <div className="flex items-center gap-1.5">
                <span className={dark ? 'text-matdash-primary' : 'text-matdash-muted'}><MoonIcon /></span>
                <DarkToggle />
                <span className={dark ? 'text-gray-500' : 'text-amber-400'}><SunIcon /></span>
              </div>

              {/* Profile avatar + dropdown */}
              <div
                ref={profileRef}
                className="relative"
                onMouseEnter={() => setProfileOpen(true)}
                onMouseLeave={() => setProfileOpen(false)}
              >
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md-lg px-1.5 py-1 hover:bg-matdash-primary-subtle transition-colors"
                  onClick={() => setProfileOpen((v) => !v)}
                  aria-expanded={profileOpen}
                  aria-haspopup="menu"
                >
                  <img
                    src={avatarImage}
                    alt=""
                    className="h-9 w-9 rounded-full object-cover border border-matdash-border bg-matdash-primary-subtle"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/logo.png';
                    }}
                  />
                  <div className="min-w-0 hidden sm:block text-left">
                    <p className={`text-sm font-semibold truncate max-w-[140px] ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                      {userName}
                    </p>
                    <p className={`text-xs capitalize truncate ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>
                      {portalLabel}
                    </p>
                  </div>
                </button>

                {profileOpen && (
                  <div role="menu" className="absolute right-0 top-full pt-1 z-50">
                    <div className={`min-w-[200px] border rounded-md-lg shadow-matdash py-1
                      ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-matdash-border'}`}>
                      <Link
                        to={accountSettingsPath}
                        role="menuitem"
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors
                          ${dark ? 'text-gray-300 hover:bg-gray-800' : 'text-matdash-dark hover:bg-matdash-primary-subtle'}`}
                        onClick={() => setProfileOpen(false)}
                      >
                        <Cog6ToothIcon className="h-4 w-4 text-matdash-muted" />
                        Account Settings
                      </Link>
                      <button
                        type="button"
                        role="menuitem"
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-matdash-danger hover:bg-matdash-danger-subtle transition-colors"
                        onClick={() => { setProfileOpen(false); setSignOutOpen(true); }}
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile hamburger */}
              <button
                type="button"
                className={`md:hidden p-2 rounded-md-lg border transition-colors
                  ${dark ? 'border-gray-700 text-gray-300' : 'border-matdash-border text-matdash-muted'}`}
                onClick={() => setMobileNavOpen((v) => !v)}
                aria-label="Toggle navigation"
              >
                {mobileNavOpen ? <XMarkIcon className="h-5 w-5" /> : <MenuIcon />}
              </button>
            </div>
          </div>

          {/* Mobile nav drawer */}
          {mobileNavOpen && (
            <div className={`md:hidden py-3 border-t ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <MobileNavLink key={item.to} {...item} onClick={closeMobile} />
                ))}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* ── Page content ─────────────────────────────────────── */}
      {/*
        py-0 on main so the nav bar sits flush against the top bar.
        The nav bar itself keeps py-2 for its own height.
        px stays for page padding.
      */}
      <main className={`flex-1 px-4 sm:px-6 pt-0 pb-24 lg:pb-6 ${dark ? 'bg-gray-950' : ''}`}>

        {/* Horizontal nav bar — flush against the header, no gap above */}
        <div className={`hidden md:flex items-center justify-start gap-2 py-2 border-b
          ${dark ? 'border-gray-800 bg-gray-900' : 'border-matdash-border bg-white'}
          -mx-4 sm:-mx-6 px-4 sm:px-6`}>
          <nav className="flex items-center gap-1">
            {navItems.map((item) => (
              <ShellNavLink key={item.to} {...item} />
            ))}
          </nav>
        </div>

        <div className="pt-4">
          <Outlet />
        </div>
      </main>

      {/* ── Footer ─────────────────────────────────────────── */}
      <footer className={`border-t px-4 py-4 text-center text-xs
        ${dark ? 'border-gray-800 bg-gray-900 text-gray-500' : 'border-matdash-border bg-white text-matdash-muted'}`}>
        <span className="inline-flex items-center gap-2">
          <img src="/logo.png" alt="" className="h-5 w-5 rounded-full" />
          Powered by Snwolley AI
        </span>
      </footer>

      {/* ── Sign-out modal ──────────────────────────────────── */}
      {signOutOpen && (
        <div className="md-modal-overlay">
          <div className={`md-modal ${dark ? '!bg-gray-900' : ''}`} role="dialog" aria-modal="true" aria-labelledby="sign-out-title">
            <div className={`md-modal-header ${dark ? '!bg-gray-900 !border-gray-700' : ''}`}>
              <h2 id="sign-out-title" className={`text-lg font-semibold mb-0 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                Sign out
              </h2>
              <button type="button" onClick={() => setSignOutOpen(false)}
                className="text-matdash-muted hover:text-matdash-dark" aria-label="Close">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className={`md-modal-body ${dark ? 'text-gray-300' : ''}`}>
              <p className="text-sm text-matdash-muted mb-0">
                Are you sure you want to sign out of CourtScribe?
              </p>
            </div>
            <div className={`md-modal-footer justify-end ${dark ? '!bg-gray-900 !border-gray-700' : ''}`}>
              <button type="button" onClick={() => setSignOutOpen(false)} className="md-btn md-btn-outline md-btn-sm">
                Cancel
              </button>
              <button type="button" onClick={handleConfirmSignOut} className="md-btn md-btn-danger md-btn-sm">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShell;
